import { Component } from "react";
import { connect } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { Preloading } from "../../components/UI/Preloader/action";
import { AUTOMATION_TASK_OPTIONS, TASK_CONFIG } from "./constants";
import { TASK_TYPES } from "./actionTypes";
import {
  fetchSupportCaseDetails,
  deleteFinancialFormData,
  deleteExecutiveSummaryFinancialData,
  takeSupportAutomationBackup,
  clearSupportWorkAutomationState,
} from "./action";
import {
  mapAutomationResponse,
  getErrorMessage,
  validateCaseId,
  validateDeleteAction,
  isDeleteTask,
} from "./helper";
import DeleteTaskContent from "./components/DeleteTaskContent";

const initialFormState = {
  selectedTask: "",
  caseId: "",
};

export class SupportWorkAutomation extends Component {
  state = {
    ...initialFormState,
    confirmModal: false,
    confirmAction: null,
  };

  componentDidUpdate(prevProps) {
    const { supportWork } = this.props;
    const prevSupportWork = prevProps.supportWork;

    if (
      supportWork.caseDetails &&
      supportWork.caseDetails !== prevSupportWork.caseDetails
    ) {
      if (supportWork.noBackupData) {
        toast.warning(
          <div>
            <i className="fa fa-exclamation-triangle"></i>&nbsp;
            {getErrorMessage(supportWork.error) ||
              "Case fetched, but no deletable data found."}
          </div>
        );
      } else {
        toast.success(
          <div>
            <i className="fa fa-check-circle fa-lg"></i>&nbsp;Case details fetched
            successfully
          </div>
        );
      }
    }

    if (
      supportWork.error &&
      supportWork.error !== prevSupportWork.error &&
      !(
        supportWork.caseDetails &&
        supportWork.caseDetails !== prevSupportWork.caseDetails &&
        supportWork.noBackupData
      )
    ) {
      if (supportWork.noBackupData) {
        toast.warning(
          <div>
            <i className="fa fa-exclamation-triangle"></i>&nbsp;
            {getErrorMessage(supportWork.error)}
          </div>
        );
      } else {
        toast.error(
          <div>
            <i className="fa fa-exclamation-triangle"></i>&nbsp;
            {getErrorMessage(supportWork.error)}
          </div>
        );
      }
    }

    if (
      supportWork.isBackupTaken &&
      supportWork.isBackupTaken !== prevSupportWork.isBackupTaken
    ) {
      toast.success(
        <div>
          <i className="fa fa-check-circle fa-lg"></i>&nbsp;Backup taken
          successfully
          {supportWork.backupFileName
            ? ` (${supportWork.backupFileName})`
            : ""}
        </div>
      );
    }

    if (
      supportWork.deleteResponse &&
      supportWork.deleteResponse !== prevSupportWork.deleteResponse
    ) {
      toast.success(
        <div>
          <i className="fa fa-check-circle fa-lg"></i>&nbsp;
          {supportWork.deleteResponse.message || "Data deleted successfully"}
        </div>
      );
    }
  }

  handleTaskChange = (event) => {
    this.setState({
      selectedTask: event.target.value,
      caseId: "",
    });
    this.props.clearSupportWorkAutomationState();
    this.props.preloading(false);
  };

  handleCaseIdChange = (event) => {
    this.setState({ caseId: event.target.value });
  };

  handleFetchCaseData = () => {
    const caseId = this.state.caseId.trim();
    const validationMessage = validateCaseId(caseId);

    if (!this.state.selectedTask) {
      toast.error(
        <div>
          <i className="fa fa-exclamation-triangle"></i>&nbsp;Please select a
          task
        </div>
      );
      return;
    }

    if (validationMessage) {
      toast.error(
        <div>
          <i className="fa fa-exclamation-triangle"></i>&nbsp;{validationMessage}
        </div>
      );
      return;
    }

    this.props.preloading(true);
    this.props.fetchSupportCaseDetails({
      caseId,
      taskType: this.state.selectedTask,
    });
  };

  openConfirmModal = (confirmAction) => {
    this.setState({ confirmModal: true, confirmAction });
  };

  closeConfirmModal = () => {
    this.setState({ confirmModal: false, confirmAction: null });
  };

  handleConfirmAction = () => {
    const { confirmAction } = this.state;
    this.closeConfirmModal();

    if (confirmAction === "takeBackup") {
      this.executeTakeBackup();
      return;
    }

    if (confirmAction === "delete") {
      this.executeDelete();
    }
  };

  handleTakeBackup = () => {
    if (!this.props.supportWork.caseDetails?.caseId) {
      toast.error(
        <div>
          <i className="fa fa-exclamation-triangle"></i>&nbsp;Please fetch case
          details before taking backup
        </div>
      );
      return;
    }
    this.openConfirmModal("takeBackup");
  };

  executeTakeBackup = () => {
    this.props.preloading(true);
    this.props.takeSupportAutomationBackup({
      caseId: this.state.caseId.trim(),
      taskType: this.state.selectedTask,
    });
  };

  handleDelete = () => {
    const taskConfig = TASK_CONFIG[this.state.selectedTask];
    const validationMessage = validateDeleteAction({
      ...this.props.supportWork,
      requiresBackup: taskConfig?.requiresBackup,
    });
    if (validationMessage) {
      toast.error(
        <div>
          <i className="fa fa-exclamation-triangle"></i>&nbsp;{validationMessage}
        </div>
      );
      return;
    }
    this.openConfirmModal("delete");
  };

  executeDelete = () => {
    const caseId = this.state.caseId.trim();
    this.props.preloading(true);

    if (this.state.selectedTask === TASK_TYPES.DELETE_FINANCIAL_FORM_DATA) {
      this.props.deleteFinancialFormData(caseId);
      return;
    }

    this.props.deleteExecutiveSummaryFinancialData(caseId);
  };

  handleReset = () => {
    this.setState({
      ...initialFormState,
      confirmModal: false,
      confirmAction: null,
    });
    this.props.clearSupportWorkAutomationState();
    this.props.preloading(false);
  };

  renderConfirmModal() {
    const isTakeBackup = this.state.confirmAction === "takeBackup";
    const requiresBackup =
      TASK_CONFIG[this.state.selectedTask]?.requiresBackup;

    return (
      <Modal isOpen={this.state.confirmModal} toggle={this.closeConfirmModal}>
        <ModalHeader toggle={this.closeConfirmModal}>
          {isTakeBackup ? "Confirm Take Backup" : "Confirm Delete"}
        </ModalHeader>
        <ModalBody>
          {isTakeBackup
            ? "This will create an S3 backup for the selected case. Please confirm to continue."
            : requiresBackup
            ? "This will permanently delete data for the selected case. Please confirm you have already taken the backup."
            : "This will permanently delete data for the selected case. Please confirm to continue."}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.closeConfirmModal}>
            Cancel
          </Button>
          <Button
            color={isTakeBackup ? "warning" : "danger"}
            onClick={this.handleConfirmAction}
          >
            {isTakeBackup ? "Take Backup" : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  renderTaskContent() {
    const { selectedTask } = this.state;
    const taskConfig = TASK_CONFIG[selectedTask];
    const { supportWork } = this.props;

    if (!taskConfig || !isDeleteTask(selectedTask)) {
      return null;
    }

    return (
      <DeleteTaskContent
        taskConfig={taskConfig}
        caseId={this.state.caseId}
        caseDetails={supportWork.caseDetails}
        isFetchingCase={supportWork.isFetchingCase}
        isDeleting={supportWork.isDeleting}
        isTakingBackup={supportWork.isTakingBackup}
        isBackupTaken={supportWork.isBackupTaken}
        backupFileName={supportWork.backupFileName}
        noBackupData={supportWork.noBackupData}
        deleteResponse={
          supportWork.deleteResponse
            ? mapAutomationResponse(supportWork.deleteResponse)
            : null
        }
        error={supportWork.error ? getErrorMessage(supportWork.error) : null}
        onCaseIdChange={this.handleCaseIdChange}
        onFetch={this.handleFetchCaseData}
        onTakeBackup={this.handleTakeBackup}
        onDelete={this.handleDelete}
        onReset={this.handleReset}
      />
    );
  }

  render() {
    const { supportWork } = this.props;
    const isBusy =
      supportWork.isFetchingCase ||
      supportWork.isDeleting ||
      supportWork.isTakingBackup;

    return (
      <Col className="px-5">
        <ToastContainer />
        {this.renderConfirmModal()}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Support Work Automation</h4>
        </div>
        <Card>
          <CardBody>
            <Row>
              <Col sm={6}>
                <FormGroup>
                  <Label for="automationTask">Select Task</Label>
                  <Input
                    type="select"
                    name="automationTask"
                    id="automationTask"
                    value={this.state.selectedTask}
                    disabled={isBusy}
                    onChange={this.handleTaskChange}
                  >
                    {AUTOMATION_TASK_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>
        {this.renderTaskContent()}
      </Col>
    );
  }
}

const mapStateToProps = (state) => ({
  supportWork: state.supportWorkAutomation || {},
});

const mapDispatchToProps = (dispatch) => ({
  preloading: (data) => dispatch(Preloading(data)),
  fetchSupportCaseDetails: (payload) =>
    dispatch(fetchSupportCaseDetails(payload)),
  takeSupportAutomationBackup: (payload) =>
    dispatch(takeSupportAutomationBackup(payload)),
  deleteFinancialFormData: (caseId) =>
    dispatch(deleteFinancialFormData(caseId)),
  deleteExecutiveSummaryFinancialData: (caseId) =>
    dispatch(deleteExecutiveSummaryFinancialData(caseId)),
  clearSupportWorkAutomationState: () =>
    dispatch(clearSupportWorkAutomationState()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SupportWorkAutomation);
